import { DatePicker, Input } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState, useCallback } from 'react';
import { capitalizeFirstLetter, getStatusText } from '../../../utils';
import { showErrorMessage, showSuccessMessage } from '../../../utils/index';
import { performItemAction } from './quotationService';
import {
  QUOTATION_BUYER_ITEM_ACTIONS,
  QUOTATION_MODAL_TYPE,
  QUOTATION_TABLE_TYPE,
} from '../../../constants';

const DECIMAL_INPUT_PATTERN = /^\d+(\.\d{0,2})?$/;
const DATE_PARSE_FORMATS = [
  'DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD',
  'MM-DD-YYYY', 'MM/DD/YYYY', 'YYYY-DD-MM', 'YYYY/DD/MM',
  'DD MMM YYYY', 'DD MMMM YYYY', 'DD MMM YY', 'DD MMMM YY',
  'MMM DD, YYYY', 'MMMM DD, YYYY', 'MMM DD YYYY', 'MMMM DD YYYY',
  'MMM DD, YY', 'MMMM DD, YY',
];

const isActionDisabled = (item) =>
  item?.itemStatus === 'approved' || item?.itemStatus === 'pending' || item?.itemStatus === 'withdrawn';

const displayValue = (value) => (value === 0 || value === '0' ? '' : value);

const canBuyerAccept = (item) => item?.itemStatus === 'submitted';

const canBuyerNegotiate = (item) => item?.itemStatus === 'submitted';

const canBuyerReject = (item) => item?.itemStatus === 'submitted';

const isFinalStatus = (item) => {
  return ['approved', 'withdrawn', 'pending'].includes(item?.itemStatus);
};

const parseDate = (date) => {
  if (!date) return null;
  if (dayjs.isDayjs(date)) return date;
  if (dayjs(date).isValid()) return dayjs(date);

  for (const format of DATE_PARSE_FORMATS) {
    const parsed = dayjs(date, format, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
};

const formatSupplyDate = (supplyDate) => {
  if (!supplyDate) return '';
  const parsed = parseDate(supplyDate);
  return parsed ? parsed.format('DD/MM/YYYY') : supplyDate;
};

const EMPTY_ITEMS = [];
const EMPTY_REMOVED_ITEMS = [];

const getItemsSyncKey = (itemList) =>
  (itemList || [])
    .map(
      (i) =>
        `${i._id ?? ''}:${i.itemRef ?? ''}:${i.itemStatus ?? ''}:${i.quotedPrice ?? ''}:${i.negotiatedPrice ?? ''}:${i.availableQuantity ?? ''}:${i.supplyDate ?? ''}`
    )
    .join('|');

const getNegotiatedPriceError = (quoteData, index, item) => {
  const price = parseFloat(item.negotiatedPrice);
  if (Number.isNaN(price)) return null;

  const quoted = parseFloat(item.quotedPrice);
  if (!Number.isNaN(quoted) && price >= quoted) {
    return 'Must be less than quoted price';
  }

  const requested = quoteData?.items?.[index];
  const minBudget = requested?.budget?.min;
  if (requested && !requested.openPrice && minBudget != null && minBudget !== '') {
    const min = parseFloat(minBudget);
    if (!Number.isNaN(min) && price < min) {
      return 'Cannot be lower than min budget';
    }
  }
  return null;
};

export default function TableComponent({
  type,
  modalType,
  quoteData,
  actionAPI,
  items: itemsProp,
  itemsStatus,
  updateClicked,
  submittedItems,
  removedItems: removedItemsProp,
  saveAllItems,
  handleDeleteForItem,
  handleRestore,
  openHistoryModal,
  handleInputChange,
  handleDateChange,
  handleItemStatus,
  onRequestReject,
  showUpdateButton,
  setUpdateClicked,
  onStartNegotiate,
  quoteRelationId,
  onItemActionComplete,
  userRole,
}) {
  const items = itemsProp ?? EMPTY_ITEMS;
  const removedItems = removedItemsProp ?? EMPTY_REMOVED_ITEMS;

  const itemsSyncKey = useMemo(() => getItemsSyncKey(items), [items]);

  const [localItems, setLocalItems] = useState(items);
  const [emptyFields, setEmptyFields] = useState({ items: [], removedItems: [] });
  const [activeIndex, setActiveIndex] = useState(null);
  const [negotiatingItemId, setNegotiatingItemId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  React.useEffect(() => {
    setLocalItems(items);
    setNegotiatingItemId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when item data changes, not array reference
  }, [itemsSyncKey]);

  const isManager = userRole === 'manager';
  const isUser = userRole === 'user' || !userRole;

  const handleBuyerAccept = useCallback(async (lineItem) => {
    if (!quoteRelationId) return;
    const lineItemId = lineItem?._id;
    if (!lineItemId) return;
    setActionLoading(true);
    try {
      const response = await performItemAction(
        quoteRelationId,
        lineItemId,
        QUOTATION_BUYER_ITEM_ACTIONS.ACCEPT
      );
      if (response.ok) {
        showSuccessMessage(`Item "${lineItem.name}" accepted`);
        setLocalItems((prev) =>
          prev.map((i) => (i._id === lineItemId ? { ...i, itemStatus: 'accepted' } : i))
        );
        onItemActionComplete?.();
      } else {
        showErrorMessage(response.message || 'Failed to accept item');
      }
    } catch (error) {
      showErrorMessage('Failed to accept item');
    }
    setActionLoading(false);
  }, [quoteRelationId, onItemActionComplete]);

  const handleBuyerReject = useCallback(
    (lineItem) => {
      if (!onRequestReject) return;
      onRequestReject(lineItem);
    },
    [onRequestReject]
  );

  const startNegotiating = useCallback((itemId) => {
    setNegotiatingItemId(itemId);
  }, []);

  const handleNegotiatedPriceChange = useCallback((itemId, value) => {
    setLocalItems((prev) =>
      prev.map((i) => (i._id === itemId ? { ...i, negotiatedPrice: value } : i))
    );
  }, []);

  const handleNegotiatedPriceInput = useCallback(
    (itemId) => (e) => {
      const value = e.target.value;
      if (value === '' || DECIMAL_INPUT_PATTERN.test(value)) {
        handleNegotiatedPriceChange(itemId, value);
      }
    },
    [handleNegotiatedPriceChange]
  );

  const getLocalItem = useCallback(
    (item) => localItems.find((i) => i._id === item._id) || item,
    [localItems]
  );

  const handleNegotiateSubmit = useCallback(async (lineItem) => {
    if (!quoteRelationId) return;
    const lineItemId = lineItem?._id;
    if (!lineItemId) return;

    const localItem = getLocalItem(lineItem);
    const negotiatedPrice = localItem.negotiatedPrice;
    if (!negotiatedPrice && negotiatedPrice !== 0) {
      showErrorMessage('Please enter a negotiated price');
      return;
    }
    
    const error = getNegotiatedPriceError(quoteData, localItems.indexOf(localItem), localItem);
    if (error) {
      showErrorMessage(error);
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await performItemAction(quoteRelationId, lineItemId, QUOTATION_BUYER_ITEM_ACTIONS.NEGOTIATE, {
        negotiatedPrice: parseFloat(negotiatedPrice),
      });
      if (response.ok) {
        showSuccessMessage(`Negotiation sent for "${lineItem.name}"`);
        setLocalItems((prev) =>
          prev.map((i) =>
            i._id === lineItemId
              ? { ...i, itemStatus: 'negotiated', negotiatedPrice: parseFloat(negotiatedPrice) }
              : i
          )
        );
        setNegotiatingItemId(null);
        onItemActionComplete?.();
      } else {
        showErrorMessage(response.message || 'Failed to send negotiation');
      }
    } catch (error) {
      showErrorMessage('Failed to send negotiation');
    }
    setActionLoading(false);
  }, [quoteRelationId, quoteData, localItems, getLocalItem, onItemActionComplete]);

  const isViewMode = type === QUOTATION_TABLE_TYPE.VIEW;
  const isEditMode = type === QUOTATION_TABLE_TYPE.EDIT;
  const isListApprovals = modalType === QUOTATION_MODAL_TYPE.LIST_APPROVALS;
  const showHistoryLink = !modalType;

  const negotiatingEntries = useMemo(
    () =>
      items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.isNegotiating),
    [items]
  );

  const acceptedTableItems = useMemo(
    () => localItems.filter((item) => item.itemStatus !== ''),
    [localItems]
  );

  const checkEmptyFields = (itemList, key, { negotiateOnly = false } = {}) => {
    let partialRows = [];
    const fullyBlankRows = [];

    itemList.forEach((item, index) => {
      const fields = negotiateOnly
        ? [item.negotiatedPrice]
        : [item.availableQuantity, item.supplyDate, item.quotedPrice];
      const filled = fields.filter(Boolean);

      if (filled.length > 0 && filled.length < fields.length) {
        partialRows.push(index);
      }
      if (fields.every((field) => !field)) {
        fullyBlankRows.push(index);
      }
    });

    setEmptyFields((prev) => ({ ...prev, [key]: partialRows }));
    return partialRows.length === 0 && fullyBlankRows.length !== itemList.length;
  };

  const validateQuantityAndDate = (itemList, indexOffset = 0) => {
    for (let i = 0; i < itemList.length; i++) {
      const item = itemList[i];
      const index = indexOffset + i;
      const requested = quoteData?.items?.[index];

      if (
        requested?.quantity &&
        item.availableQuantity &&
        parseFloat(item.availableQuantity) > parseFloat(requested.quantity)
      ) {
        showErrorMessage(`Item ${index + 1}: Available quantity cannot exceed requested quantity`);
        return false;
      }

      if (
        requested?.supplyDate &&
        item.supplyDate &&
        dayjs(item.supplyDate).isAfter(dayjs(requested.supplyDate), 'day')
      ) {
        showErrorMessage(`Item ${index + 1}: Supply date cannot be after requested date`);
        return false;
      }

      if (
        requested?.dueDate &&
        item.supplyDate &&
        dayjs(item.supplyDate).isBefore(dayjs(requested.dueDate), 'day')
      ) {
        showErrorMessage(`Item ${index + 1}: Supply date cannot be before due date`);
        return false;
      }
    }
    return true;
  };

  const validateNegotiatingItems = () => {
    for (const { item, index } of negotiatingEntries) {
      const error = getNegotiatedPriceError(quoteData, index, item);
      if (error) {
        showErrorMessage(`Item ${index + 1}: ${error}`);
        return false;
      }
    }
    return true;
  };

  const handleSaveAllClick = () => {
    const isValid = checkEmptyFields(items, 'items');
    if (!isValid) {
      showErrorMessage('Please complete all fields for each quote before saving.');
      return;
    }
    if (!validateQuantityAndDate(items)) return;
    saveAllItems();
  };

  const checkNegotiateFields = () => {
    const missingRows = negotiatingEntries
      .filter(({ item }) => !item.negotiatedPrice && item.negotiatedPrice !== 0)
      .map(({ index }) => index);

    setEmptyFields((prev) => ({ ...prev, items: missingRows }));
    return missingRows.length === 0;
  };

  const handleSaveNegotiateClick = () => {
    if (!checkNegotiateFields()) {
      showErrorMessage('Please enter negotiated price for each item before saving.');
      return;
    }
    if (!validateNegotiatingItems()) return;
    saveAllItems();
  };

  const handleRestoreClick = () => {
    if (!checkEmptyFields(removedItems, 'removedItems')) {
      showErrorMessage('Please complete all fields for each quote before saving.');
      return;
    }
    if (!validateQuantityAndDate(removedItems)) return;
    handleRestore();
  };

  const handleUpdateClick = (index) => {
    setUpdateClicked(true);
    setActiveIndex(index);
  };

  const handleNegotiateClick = (index) => {
    onStartNegotiate?.(index);
  };

  const handleDecimalInput = (onChange) => (e) => {
    const value = e.target.value;
    if (value === '' || DECIMAL_INPUT_PATTERN.test(value)) {
      onChange(e);
    }
  };

  const getSupplyDateDisabledDate = (index) => (current) => {
    if (!current) return false;
    const requested = quoteData?.items?.[index];
    if (requested?.dueDate && current.isBefore(dayjs(requested.dueDate), 'day')) {
      return true;
    }
    if (requested?.supplyDate && current.isAfter(dayjs(requested.supplyDate), 'day')) {
      return true;
    }
    return false;
  };

  const renderActionLinks = (item, index) => {
    const showAccept = canBuyerAccept(item) && isUser;
    const showReject = canBuyerReject(item) && isUser;
    const showNegotiate = canBuyerNegotiate(item) && isUser;
    const isCurrentlyNegotiating = negotiatingItemId === item._id;
    const rowItem = getLocalItem(item);
    const negotiatedError =
      isCurrentlyNegotiating && getNegotiatedPriceError(quoteData, index, rowItem);

    return (
      <div className="Quotation_actionCell">
        {showAccept && !isCurrentlyNegotiating && (
          <span 
            className="quoteRemove" 
            style={{ color: '#52C41A' }}
            onClick={!actionLoading ? () => handleBuyerAccept(item) : undefined}
          >
            Accept
          </span>
        )}

        {showReject && !isCurrentlyNegotiating && (
          <span
            className="quoteRemove"
            style={{ color: '#FF4D4F' }}
            onClick={!actionLoading ? () => handleBuyerReject(item) : undefined}
          >
            Reject
          </span>
        )}
        
        {showNegotiate && !isCurrentlyNegotiating && (
          <span 
            className="quoteRemove" 
            style={{ color: '#722ED1' }}
            onClick={!actionLoading ? () => startNegotiating(item._id) : undefined}
          >
            Negotiate
          </span>
        )}
        
        {isCurrentlyNegotiating && (
          <>
            <span
              className={`quoteRemove${negotiatedError ? ' disabled' : ''}`}
              style={{ color: negotiatedError ? undefined : '#722ED1' }}
              onClick={
                !actionLoading && !negotiatedError ? () => handleNegotiateSubmit(item) : undefined
              }
            >
              Submit
            </span>
            <span 
              className="quoteRemove" 
              style={{ color: '#8C8C8C' }}
              onClick={() => setNegotiatingItemId(null)}
            >
              Cancel
            </span>
          </>
        )}
        
        {item?.itemStatus === 'accepted' && (
          <span style={{ ...getStatusText('accepted'), fontStyle: 'italic' }}>Accepted</span>
        )}
        
        {item?.itemStatus === 'negotiated' && (
          <span style={{ ...getStatusText('negotiated'), fontStyle: 'italic' }}>Waiting for seller</span>
        )}
        
        {item?.itemStatus === 'pending' && !isManager && (
          <span style={{ ...getStatusText('pending'), fontStyle: 'italic' }}>Pending approval</span>
        )}
        
        {item?.itemStatus === 'pending' && isManager && actionAPI && (
          <>
            <span
              className="quoteRemove"
              style={{ paddingRight: '20px' }}
              onClick={!actionLoading ? () => handleItemStatus?.(item, actionAPI, 'approved') : undefined}
            >
              Approve
            </span>
            <span
              className="quoteRemove"
              onClick={!actionLoading ? () => onRequestReject?.(item) : undefined}
            >
              Reject
            </span>
          </>
        )}
        
        {item?.itemStatus === 'approved' && (
          <span style={{ ...getStatusText('approved'), fontStyle: 'italic' }}>Approved</span>
        )}
        
        {item?.itemStatus === 'rejected' && (
          <span style={{ ...getStatusText('rejected'), fontStyle: 'italic' }}>Rejected</span>
        )}

        {item?.itemStatus === 'withdrawn' && (
          <span style={{ ...getStatusText('withdrawn'), fontStyle: 'italic' }}>Withdrawn</span>
        )}
      </div>
    );
  };

  const renderRequestedSection = () => (
    <section className="Quotation_section">
      <div className="Quotation_subtitle">
        <span>Quotation Requested</span>
        {showHistoryLink && <span onClick={openHistoryModal}>View History</span>}
      </div>
      <table className="Quotation_table">
        <thead>
          <tr>
            <th>Sr.</th>
            <th>Item</th>
            <th>Quantity</th>
            <th>Due Date</th>
            <th>Min Budget</th>
            <th>Max Budget</th>
            <th>Place of Supply</th>
            <th>Supply Date</th>
            {showHistoryLink && <th>Status</th>}
          </tr>
        </thead>
        <tbody>
          {quoteData?.items?.map((item, index) => {
            const currentStatus =
              itemsStatus?.items?.length > index ? itemsStatus.items[index].itemStatus : '';
            return (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.name}</td>
                <td>{item.quantity} {item.unit}</td>
                <td>{item.dueDate ? dayjs(item.dueDate).format('DD/MM/YYYY') : ''}</td>
                <td>{item.budget?.min}</td>
                <td>{item.budget?.max}</td>
                <td>{item.placeOfSupply?.join(', ')}</td>
                <td>{item.supplyDate ? dayjs(item.supplyDate).format('DD/MM/YYYY') : ''}</td>
                {showHistoryLink && (
                  <td style={getStatusText(currentStatus)}>
                    {capitalizeFirstLetter(currentStatus)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );

  const renderApprovalsSection = () => (
    <section className="Quotation_section">
      <div className="Quotation_subtitle">
        <span>Quotation Accepted</span>
      </div>
      <table className="Quotation_table">
        <thead>
          <tr>
            <th>Sr.</th>
            <th>Item</th>
            <th>Available Quantity</th>
            <th>Supply Date</th>
            <th>Quoted Price</th>
            <th>Negotiated Price</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {localItems?.map((item, index) =>
            item.itemStatus !== '' ? (
              <tr key={item.itemRef || index}>
                <td>{index + 1}</td>
                <td>{item.name}</td>
                <td>{displayValue(item.availableQuantity)}</td>
                <td>{formatSupplyDate(item.supplyDate)}</td>
                <td>{displayValue(item.quotedPrice)}</td>
                <td>{displayValue(item.negotiatedPrice)}</td>
                <td>
                  {item.itemStatus === '' ? (
                    <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>Removed</span>
                  ) : (
                    <>
                      {item.itemStatus === 'pending' && actionAPI && (
                        <>
                          <span
                            className="quoteRemove"
                            style={{ paddingRight: '20px' }}
                            onClick={() => {
                              handleItemStatus(item, actionAPI, 'approved').then((ok) => {
                                if (!ok) return;
                                setLocalItems((prev) => {
                                  const next = [...prev];
                                  next[index] = { ...next[index], itemStatus: 'approved' };
                                  return next;
                                });
                              });
                            }}
                          >
                            Approve
                          </span>
                          <span
                            className="quoteRemove"
                            onClick={() => onRequestReject?.(item)}
                          >
                            Reject
                          </span>
                        </>
                      )}
                      {item.itemStatus === 'approved' && (
                        <span style={getStatusText('approved')}>Approved</span>
                      )}
                      {item.itemStatus === 'rejected' && (
                        <span style={getStatusText('rejected')}>Rejected</span>
                      )}
                      {!['pending', 'approved', 'rejected'].includes(item.itemStatus) && (
                        <span style={getStatusText(item.itemStatus)}>
                          {item.itemStatus || '—'}
                        </span>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ) : null
          )}
        </tbody>
      </table>
    </section>
  );

  const renderAcceptedTable = () => (
    <>
      <table className="Quotation_table">
        <thead>
          <tr>
            <th>Sr.</th>
            <th>Item</th>
            <th>Available Quantity</th>
            <th>Supply Date</th>
            <th>Quoted Price</th>
            <th>Negotiated Price</th>
            <th className="Quotation_actionColumn">Action</th>
          </tr>
        </thead>
        <tbody>
          {acceptedTableItems.map((item, rowIndex) => {
            const index = localItems.findIndex((i) => i._id === item._id);
            const resolvedIndex = index >= 0 ? index : items.findIndex((i) => i._id === item._id);
            const isNegotiating = negotiatingItemId === item._id;

            if (isNegotiating) {
              return null;
            }

            const rowItem = getLocalItem(item);
            return (
              <tr key={item.itemRef || index}>
                <td>{rowIndex + 1}</td>
                <td>{rowItem?.name}</td>
                <td>{displayValue(rowItem?.availableQuantity)}</td>
                <td>{formatSupplyDate(rowItem?.supplyDate)}</td>
                <td>{displayValue(rowItem?.quotedPrice)}</td>
                <td>{displayValue(rowItem?.negotiatedPrice)}</td>
                <td className="Quotation_actionColumn">{renderActionLinks(rowItem, resolvedIndex)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {acceptedTableItems.map((item) => {
        if (negotiatingItemId !== item._id) return null;
        const index = localItems.findIndex((i) => i._id === item._id);
        const resolvedIndex = index >= 0 ? index : items.findIndex((i) => i._id === item._id);
        const rowItem = getLocalItem(item);
        return (
          <div key={`negotiate-${item.itemRef || item._id}`} className="Quotation_negotiateRow">
            {renderItemFormRow({
              item: rowItem,
              index: resolvedIndex,
              listKey: 'item',
              negotiateOnly: true,
              autoFocus: true,
              onNegotiatedPriceInput: handleNegotiatedPriceInput(item._id),
              actionSlot: renderActionLinks(rowItem, resolvedIndex),
            })}
          </div>
        );
      })}
    </>
  );

  const renderItemFormRow = ({
    item,
    index,
    listKey,
    negotiateOnly = false,
    autoFocus = false,
    onNegotiatedPriceInput,
    actionSlot,
  }) => {
    const readOnly = negotiateOnly || item.itemStatus === 'approved';
    const negotiatedError = negotiateOnly && getNegotiatedPriceError(quoteData, index, item);
    const exceedsQty =
      quoteData?.items?.[index]?.quantity &&
      item.availableQuantity &&
      parseFloat(item.availableQuantity) > parseFloat(quoteData.items[index].quantity);

    return (
      <div
        className={`${activeIndex === index ? 'active-input ' : ''}Quotation_eachFormRow${
          negotiateOnly ? ' Quotation_eachFormRow--negotiate' : ''
        }`}
        key={item.itemRef || `${listKey}-${index}`}
      >
        <div className="Quotation_eachForm">
          <label>Item</label>
          <Input value={item.name} disabled onChange={handleInputChange(index, 'name', listKey)} />
        </div>

        {!negotiateOnly && (
          <>
            <div className="Quotation_eachForm">
              <label>Available Quantity</label>
              <Input
                placeholder="Type here..."
                autoFocus={autoFocus}
                disabled={readOnly}
                value={displayValue(item.availableQuantity)}
                onChange={handleDecimalInput(handleInputChange(index, 'availableQuantity', listKey))}
                style={{
                  borderColor:
                    (emptyFields.items.includes(index) && !item.availableQuantity) || exceedsQty
                      ? '#FF4D4F'
                      : '',
                }}
              />
              {exceedsQty && (
                <span style={{ color: '#FF4D4F', fontSize: '12px' }}>
                  Cannot Exceed Requested Quantity
                </span>
              )}
            </div>

            <div className="Quotation_eachForm">
              <label>Supply Date</label>
              <DatePicker
                value={parseDate(item.supplyDate)}
                disabled={readOnly}
                onChange={handleDateChange(index, 'supplyDate', listKey)}
                disabledDate={getSupplyDateDisabledDate(index)}
                style={{
                  borderColor: emptyFields.items.includes(index) && !item.supplyDate ? '#FF4D4F' : '',
                }}
              />
            </div>

            <div className="Quotation_eachForm">
              <label>Quoted Price</label>
              <Input
                placeholder="Type here..."
                disabled={readOnly}
                value={displayValue(item.quotedPrice)}
                onChange={handleDecimalInput(handleInputChange(index, 'quotedPrice', listKey))}
                style={{
                  borderColor: emptyFields.items.includes(index) && !item.quotedPrice ? '#FF4D4F' : '',
                }}
              />
            </div>
          </>
        )}

        {negotiateOnly && (
          <>
            <div className="Quotation_eachForm">
              <label>Available Quantity</label>
              <Input value={displayValue(item.availableQuantity)} disabled />
            </div>
            <div className="Quotation_eachForm">
              <label>Supply Date</label>
              <Input value={formatSupplyDate(item.supplyDate)} disabled />
            </div>
            <div className="Quotation_eachForm">
              <label>Quoted Price</label>
              <Input value={displayValue(item.quotedPrice)} disabled />
            </div>
            <div className="Quotation_eachForm">
              <label>Negotiated Price</label>
              <Input
                placeholder="Type here..."
                inputMode="decimal"
                autoComplete="off"
                autoFocus={autoFocus}
                disabled={item.itemStatus === 'approved'}
                value={item.negotiatedPrice ?? ''}
                onChange={
                  onNegotiatedPriceInput
                    ? onNegotiatedPriceInput
                    : handleDecimalInput(handleInputChange(index, 'negotiatedPrice', listKey))
                }
                style={{
                  borderColor:
                    (emptyFields.items.includes(index) && !item.negotiatedPrice) || negotiatedError
                      ? '#FF4D4F'
                      : '',
                }}
              />
              {negotiatedError && (
                <span style={{ color: '#FF4D4F', fontSize: '12px' }}>{negotiatedError}</span>
              )}
            </div>
            {actionSlot && (
              <div className="Quotation_eachForm Quotation_actionForm">
                <label>Action</label>
                {actionSlot}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSubmittedForm = () => (
    <div>
      <div className="Quotation_subtitle">
        <span>Quotation Submitted</span>
        <span onClick={handleSaveAllClick}>Update all</span>
      </div>
      {acceptedTableItems.map((item) => {
        const index = items.indexOf(item);
        return renderItemFormRow({
          item,
          index,
          listKey: 'item',
          autoFocus: index === activeIndex,
        });
      })}
    </div>
  );

  const renderNegotiateSection = () =>
    negotiatingEntries.length > 0 && (
      <section className="Quotation_section">
        <div className="Quotation_subtitle">
          <span>Quotation Negotiated</span>
          <span onClick={handleSaveNegotiateClick}>Update all</span>
        </div>
        {negotiatingEntries.map(({ item, index }) =>
          renderItemFormRow({
            item,
            index,
            listKey: 'item',
            negotiateOnly: true,
            autoFocus: true,
          })
        )}
      </section>
    );

  const renderRemovedSection = () =>
    removedItems.length > 0 && (
      <section className="Quotation_section">
        <div className="Quotation_subtitle">
          <span>Quotation Removed</span>
          <span onClick={handleRestoreClick}>Save all</span>
        </div>
        {removedItems.map((item, index) =>
          renderItemFormRow({ item, index, listKey: 'removeItem' })
        )}
      </section>
    );

  const renderEditSection = () => {
    if (!items.length) return null;

    const showAcceptedTable = !updateClicked;

    return (
      <section className="Quotation_section">
        {showAcceptedTable ? (
          <div>
            <div className="Quotation_subtitle">
              <span>Quotation Submitted</span>
            </div>
            {renderAcceptedTable()}
          </div>
        ) : (
          renderSubmittedForm()
        )}
        {renderNegotiateSection()}
      </section>
    );
  };

  const showRequestedSection = quoteData && (isViewMode || isEditMode);

  return (
    <>
      {showRequestedSection && renderRequestedSection()}
      {isListApprovals && items?.length > 0 && renderApprovalsSection()}
      {isEditMode && !isListApprovals && renderEditSection()}
      {renderRemovedSection()}
    </>
  );
}
